import { Alert, Button, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { InviteLinkDialog } from '../../components/InviteLinkDialog';
import { apiErrorMessage } from '../../lib/apiError';

/** Giving someone a portal login through an emailed invitation, or taking it away. */
export function PortalAccess({
  title,
  joinedText,
  inviteText,
  joined: alreadyJoined,
  invite,
  remove,
}: {
  title: string;
  joinedText: string;
  inviteText: string;
  joined: boolean;
  /** Returns the invitation's link, to show once. */
  invite: () => Promise<string | undefined>;
  remove: () => Promise<unknown>;
}) {
  const queryClient = useQueryClient();
  const [link, setLink] = useState<string | null>(null);
  const [joined, setJoined] = useState(alreadyJoined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={1} sx={{ mb: 3 }}>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {joined ? joinedText : inviteText}
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row">
        {joined ? (
          <Button
            size="small"
            color="error"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await remove();
                setJoined(false);
              })
            }
          >
            Remove portal access
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const url = await invite();
                if (url) setLink(url);
              })
            }
          >
            Invite to portal
          </Button>
        )}
      </Stack>
      {link && <InviteLinkDialog url={link} onClose={() => setLink(null)} />}
    </Stack>
  );
}
